// apps/web/src/services/payments.ts
//
// Collector / billing helpers around invoices and invoice_payment_attempts.
import { supabase } from '@/services/supabase';
import type { InvoicePaymentAttempt, Invoice } from '@asi/shared';

export interface PendingAttemptRow extends InvoicePaymentAttempt {
  invoice: Pick<Invoice, 'id' | 'invoice_number' | 'amount' | 'currency' | 'description' | 'due_date' | 'status'>;
  account: { id: string; name: string; account_number: string };
  submitter: { id: string; full_name: string | null; email: string | null } | null;
  reviewer: { id: string; full_name: string | null; email: string | null } | null;
}

export type AttemptStatusFilter = 'pending' | 'approved' | 'rejected' | 'all';

export interface FetchAttemptsParams {
  status?: AttemptStatusFilter;
  search?: string;
  /** ISO date (YYYY-MM-DD), inclusive lower bound on created_at */
  fromDate?: string;
  /** ISO date (YYYY-MM-DD), inclusive upper bound on created_at */
  toDate?: string;
  method?: 'qpay' | 'bank_deposit';
  limit?: number;
  offset?: number;
  sortBy?: 'created_at' | 'amount' | 'reviewed_at';
  sortDir?: 'asc' | 'desc';
}

export interface FetchAttemptsResult {
  rows: PendingAttemptRow[];
  count: number;
}

const SELECT_SHAPE = `
  *,
  invoice:invoices(id, invoice_number, amount, currency, description, due_date, status),
  account:accounts(id, name, account_number),
  submitter:profiles!invoice_payment_attempts_submitted_by_fkey(id, full_name, email),
  reviewer:profiles!invoice_payment_attempts_reviewed_by_fkey(id, full_name, email)
`;

export async function fetchPaymentAttempts(params: FetchAttemptsParams = {}): Promise<FetchAttemptsResult> {
  const {
    status = 'pending',
    search,
    fromDate,
    toDate,
    method,
    limit = 20,
    offset = 0,
    sortBy = 'created_at',
    sortDir = 'desc',
  } = params;

  let q = supabase
    .from('invoice_payment_attempts')
    .select(SELECT_SHAPE, { count: 'exact' });

  if (status !== 'all') q = q.eq('status', status);
  if (method)   q = q.eq('method', method);
  if (fromDate) q = q.gte('created_at', `${fromDate}T00:00:00`);
  if (toDate)   q = q.lte('created_at', `${toDate}T23:59:59.999`);

  q = q.order(sortBy, { ascending: sortDir === 'asc' }).range(offset, offset + limit - 1);

  const { data, error, count } = await q;
  if (error) throw error;

  let rows = (data ?? []) as unknown as PendingAttemptRow[];

  // Client-side text search across nested relations (account name/number,
  // invoice number, deposit reference/bank). Server-side OR across nested
  // relations in PostgREST is awkward — this keeps the impl simple while
  // results are paginated.
  if (search && search.trim().length > 0) {
    const s = search.trim().toLowerCase();
    rows = rows.filter((r) =>
      (r.account?.name ?? '').toLowerCase().includes(s) ||
      (r.account?.account_number ?? '').toLowerCase().includes(s) ||
      (r.invoice?.invoice_number ?? '').toLowerCase().includes(s) ||
      (r.deposit_reference ?? '').toLowerCase().includes(s) ||
      (r.deposit_bank ?? '').toLowerCase().includes(s),
    );
  }

  return { rows, count: count ?? rows.length };
}

/** @deprecated use fetchPaymentAttempts({ status: 'pending' }) */
export async function fetchPendingPaymentAttempts(): Promise<PendingAttemptRow[]> {
  const { rows } = await fetchPaymentAttempts({ status: 'pending', limit: 500 });
  return rows;
}

/** @deprecated use fetchPaymentAttempts({ status: 'all' }) */
export async function fetchReviewedAttempts(limit = 50): Promise<PendingAttemptRow[]> {
  const { rows } = await fetchPaymentAttempts({ status: 'all', limit, sortBy: 'reviewed_at', sortDir: 'desc' });
  return rows.filter((r) => r.status !== 'pending');
}

/** Returns a short-lived signed URL for a receipt path (private bucket). */
export async function getReceiptSignedUrl(path: string, expiresInSeconds = 600): Promise<string> {
  const { data, error } = await supabase
    .storage
    .from('payment-receipts')
    .createSignedUrl(path, expiresInSeconds);
  if (error) throw error;
  return data.signedUrl;
}

export interface ConfirmPaymentParams {
  attemptId: string;
  action: 'approve' | 'reject';
  notes?: string;
  rejectionReason?: string;
}

export async function confirmPayment(params: ConfirmPaymentParams): Promise<void> {
  const { data, error } = await supabase.functions.invoke('confirm-payment', { body: params });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
}

export interface RefundPaymentParams {
  attemptId: string;
  reason: string;
}

export interface RefundPaymentResult {
  success: boolean;
  attemptId: string;
  refundTransactionId: string | null;
  message: string;
}

export async function refundPayment(params: RefundPaymentParams): Promise<RefundPaymentResult> {
  const { data, error } = await supabase.functions.invoke('qpay-refund', { body: params });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as RefundPaymentResult;
}

export type QPayEnvironment = 'sandbox' | 'production';

export async function getQPayEnvironment(): Promise<QPayEnvironment> {
  const { data, error } = await supabase
    .from('system_settings')
    .select('value')
    .eq('key', 'qpay_environment')
    .maybeSingle();
  if (error) throw error;
  const v = data?.value;
  const str = typeof v === 'string' ? v : (v as any);
  return str === 'production' ? 'production' : 'sandbox';
}

export async function setQPayEnvironment(env: QPayEnvironment): Promise<void> {
  const { error } = await supabase
    .from('system_settings')
    .upsert({ key: 'qpay_environment', value: env as any }, { onConflict: 'key' });
  if (error) throw error;
}

// ─── FEL (INFILE) environment ────────────────────────────────────────────────
export type FelEnvironment = 'demo' | 'production';

export async function getFelEnvironment(): Promise<FelEnvironment> {
  const { data, error } = await supabase
    .from('system_settings')
    .select('value')
    .eq('key', 'fel_environment')
    .maybeSingle();
  if (error) throw error;
  const v = data?.value;
  const str = typeof v === 'string' ? v : (v as any);
  return str === 'production' ? 'production' : 'demo';
}

export async function setFelEnvironment(env: FelEnvironment): Promise<void> {
  const { error } = await supabase
    .from('system_settings')
    .upsert({ key: 'fel_environment', value: env as any }, { onConflict: 'key' });
  if (error) throw error;
}

// ─── Patrol dispatch fee (TRIAL users) ─────────────────────────────────────────
// Stored in system_settings as { key: 'trial_patrol_dispatch_fee', value: <number> }.
// Read by the process-alert edge function on every SOS dispatch for TRIAL accounts.

export async function getPatrolDispatchFee(): Promise<number> {
  const { data, error } = await supabase
    .from('system_settings')
    .select('value')
    .eq('key', 'trial_patrol_dispatch_fee')
    .maybeSingle();
  if (error) throw error;
  const v = data?.value;
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return isFinite(n) && n > 0 ? n : 39;
}

export async function setPatrolDispatchFee(fee: number): Promise<void> {
  if (!isFinite(fee) || fee <= 0) throw new Error('La tarifa debe ser un número mayor a 0');
  const { error } = await supabase
    .from('system_settings')
    .upsert({ key: 'trial_patrol_dispatch_fee', value: fee as any }, { onConflict: 'key' });
  if (error) throw error;
}

// ─── FEL DTE emission (manual, from web) ─────────────────────────────────────
export interface EmitDteReceptorOverride {
  nit?:     string;
  name?:    string;
  address?: string;
  email?:   string;
}

export interface EmitDteParams {
  invoiceId: string;
  dteType?:  'FACT' | 'NCRE' | 'NDEB';
  receptor?: EmitDteReceptorOverride;
}

export interface EmitDteResult {
  uuid:    string;
  serie:   string | null;
  numero:  number | null;
  pdf_url: string;
  xml_url: string;
  fel_document_id: string;
  existing?: boolean;
}

export async function emitDte(params: EmitDteParams): Promise<EmitDteResult> {
  const { data, error } = await supabase.functions.invoke('fel-emit-dte', {
    body: {
      invoice_id: params.invoiceId,
      dte_type:   params.dteType ?? 'FACT',
      receptor:   params.receptor,
    },
  });
  if (error) {
    // FunctionsHttpError.context is the raw Response — parse body to get the real message.
    let body: Record<string, unknown> | null = null;
    try { body = await (error as any).context?.json?.(); } catch { /* ignore */ }
    const msg = (body?.message ?? body?.error) as string | undefined;
    throw new Error(msg ?? error.message);
  }
  if (data?.error) throw new Error(data.error + (data.detail?.descripcion_errores?.[0]?.mensaje_error ? ` — ${data.detail.descripcion_errores[0].mensaje_error}` : ''));
  return data as EmitDteResult;
}

// ─── NIT lookup against SAT (INFILE WS Consulta Receptores, 30-day cache) ───
export interface ConsultNitResult {
  valid:  boolean;
  nit:    string;
  name:   string | null;
  status: string;
  source: 'cache' | 'infile' | 'local';
  message?: string | null;
  /** Full INFILE payload — surfaces every extra receptor field
   *  (nombre_comercial, direccion, correo, regimen, etc.). */
  raw?:   Record<string, unknown>;
}

export async function consultNit(nit: string, accountId?: string): Promise<ConsultNitResult> {
  const { data, error } = await supabase.functions.invoke('fel-consult-nit', {
    body: { nit, account_id: accountId ?? null },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as ConsultNitResult;
}

// ─── DTE cancellation ───────────────────────────────────────────────────────
export interface CancelDteParams {
  felDocumentId: string;
  reason:        string;
}
export interface CancelDteResult {
  success: true;
  uuid:    string;
  cancelled_at: string;
}
export async function cancelDte(params: CancelDteParams): Promise<CancelDteResult> {
  const { data, error } = await supabase.functions.invoke('fel-cancel-dte', {
    body: { fel_document_id: params.felDocumentId, reason: params.reason },
  });
  if (error) {
    let body: Record<string, unknown> | null = null;
    try { body = await (error as any).context?.json?.(); } catch { /* ignore */ }
    const headline = (body?.error ?? body?.message) as string | undefined;
    const detail   = (body?.detail) as string | undefined;
    const composed = [headline, detail].filter(Boolean).join(' — ');
    throw new Error(composed || error.message);
  }
  if (data?.error) throw new Error([data.error, data.detail].filter(Boolean).join(' — '));
  return data as CancelDteResult;
}
